import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiArrowRight, FiTrendingUp, FiClock, FiZap } = FiIcons;

const caseStudies = [
    {
        industry: "Manufacturing",
        problem: "A mid-size manufacturer was managing production schedules, inventory, and billing across three disconnected spreadsheets — leading to stock-outs and delayed invoicing.",
        solution: "We built a unified ERP system on .NET with real-time inventory tracking, automated production scheduling, and integrated billing workflows.",
        outcomes: [
            { icon: FiClock, text: "Manual data entry reduced by 70%" },
            { icon: FiTrendingUp, text: "Order fulfilment accuracy improved to 98%" },
            { icon: FiZap, text: "Invoicing cycle cut from 5 days to same-day" }
        ],
        tag: "ERP & Production Software"
    },
    {
        industry: "Logistics",
        problem: "A logistics company was spending 40+ hours per week manually processing shipment documents and routing data extracted from PDFs and scanned images.",
        solution: "We deployed a computer vision pipeline using TensorFlow to extract, classify, and route shipment data automatically — integrated directly into their existing Azure infrastructure.",
        outcomes: [
            { icon: FiClock, text: "Document processing time reduced by 85%" },
            { icon: FiTrendingUp, text: "Processing capacity scaled to 10,000+ documents/month" },
            { icon: FiZap, text: "Zero additional headcount required" }
        ],
        tag: "AI & Computer Vision"
    },
    {
        industry: "Retail",
        problem: "A growing e-commerce brand needed a custom order management system to replace their off-the-shelf tool that couldn't handle multi-warehouse inventory and complex discount rules.",
        solution: "We engineered a bespoke order management platform with real-time stock sync across 3 warehouses, dynamic pricing rules, and a role-based admin dashboard.",
        outcomes: [
            { icon: FiClock, text: "Order processing speed increased 3x" },
            { icon: FiTrendingUp, text: "Inventory discrepancies dropped to under 1%" },
            { icon: FiZap, text: "System handling 200+ concurrent users" }
        ],
        tag: "Custom Software Development"
    }
];

const CaseStudies = () => {
    const navigate = useNavigate();

    return (
        <section className="py-24 relative z-10 bg-white">
            <div className="max-w-7xl mx-auto px-6">
                <div className="text-center mb-16">
                    <h2 className="text-3xl md:text-5xl font-bold mb-4 text-slate-900">
                        Real <span className="text-gradient-cyan">Results</span>
                    </h2>
                    <p className="text-slate-600 max-w-2xl mx-auto text-lg">
                        Anonymised snapshots of real client engagements — showing the problems we solve and the outcomes we deliver.
                    </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {caseStudies.map((study, index) => (
                        <motion.div
                            key={index}
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5, delay: index * 0.15 }}
                            className="bg-white border border-slate-200 hover:border-cyan-300 transition-all duration-300 group overflow-hidden shadow-sm hover:shadow-md flex flex-col"
                        >
                            {/* Header */}
                            <div className="p-6 border-b border-slate-100 bg-slate-50">
                                <div className="flex items-center justify-between mb-3">
                                    <span className="text-xs font-black uppercase tracking-[0.2em] text-cyan-600">{study.industry}</span>
                                    <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 bg-white px-2 py-1 border border-slate-200">{study.tag}</span>
                                </div>
                            </div>

                            {/* Content */}
                            <div className="p-6 flex-grow flex flex-col">
                                <div className="mb-4">
                                    <h4 className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-2">The Problem</h4>
                                    <p className="text-sm text-slate-700 leading-relaxed">{study.problem}</p>
                                </div>

                                <div className="mb-6">
                                    <h4 className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-2">Our Solution</h4>
                                    <p className="text-sm text-slate-600 leading-relaxed">{study.solution}</p>
                                </div>

                                {/* Outcomes */}
                                <div className="mt-auto pt-4 border-t border-slate-100">
                                    <h4 className="text-xs font-bold uppercase tracking-widest text-cyan-600 mb-3">Outcomes</h4>
                                    <div className="space-y-2">
                                        {study.outcomes.map((outcome, oIndex) => (
                                            <div key={oIndex} className="flex items-center gap-2">
                                                <SafeIcon icon={outcome.icon} className="text-cyan-500 text-sm flex-shrink-0" />
                                                <span className="text-xs text-slate-700 font-medium">{outcome.text}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>

                <motion.div
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.3 }}
                    className="text-center mt-12"
                >
                    <button
                        onClick={() => navigate('/capabilities')}
                        className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-cyan-600 hover:text-slate-900 transition-colors group"
                    >
                        See All Capabilities
                        <SafeIcon icon={FiArrowRight} className="group-hover:translate-x-1 transition-transform" />
                    </button>
                </motion.div>
            </div>
        </section>
    );
};

export default CaseStudies;
